import React, {useState} from "react";
import {withNamespaces} from "react-i18next";

import {
    useAutoplaySequence,
} from "../../../../../presentation/public-site/use-autoplay-sequence";
import {
    multimodalBenefitKeys,
    multimodalControlKeys,
    multimodalDefaultStageId,
    multimodalRouteStages,
} from "../../content/multimodal-route";
import LandingSalesCard from "../../ui/LandingSalesCard";
import LandingCheckIcon from "../../ui/LandingCheckIcon";
import "./style.less";

const MULTIMODAL_AUTOPLAY_INTERVAL_MS = 5000;

const MULTIMODAL_STAGE_IDS =
    multimodalRouteStages.map(
        ({id}) => id
    );

function StageIcon({type}) {
    if (type === "ship") {
        return (
            <svg
                width="26"
                height="26"
                viewBox="0 0 26 26"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
            >
                <path
                    d="M5 12.5L7 7.5H19L21 12.5L19.2 18.5H6.8L5 12.5Z"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinejoin="round"
                />
                <path
                    d="M9.5 7.5V4.5H16.5V7.5M3.5 20.5C5.5 22 7.5 22 9.5 20.5C11.5 22 13.5 22 15.5 20.5C17.5 22 19.5 22 22.5 20.5"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        );
    }

    if (type === "train") {
        return (
            <svg
                width="26"
                height="26"
                viewBox="0 0 26 26"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
            >
                <rect
                    x="6"
                    y="3.5"
                    width="14"
                    height="16"
                    rx="3"
                    stroke="currentColor"
                    strokeWidth="1.7"
                />
                <path
                    d="M8.5 8H17.5M9 22.5L11 19.5M17 22.5L15 19.5M9.5 15.5H9.51M16.5 15.5H16.51"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                />
            </svg>
        );
    }

    return (
        <svg
            width="26"
            height="26"
            viewBox="0 0 26 26"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
        >
            <path
                d="M3.5 6.5H15.5V18.5H3.5V6.5ZM15.5 10H20L23 13.5V18.5H15.5V10Z"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
            />
            <circle cx="8" cy="19" r="2.5" fill="white" stroke="currentColor" strokeWidth="1.7"/>
            <circle cx="19.5" cy="19" r="2.5" fill="white" stroke="currentColor" strokeWidth="1.7"/>
        </svg>
    );
}

export function MultimodalSection({t}) {
    const [activeStageId, setActiveStageId] =
        useState(multimodalDefaultStageId);

    const {
        targetRef: autoplayTargetRef,
        isAutoplaying,
        selectItem: selectStage,
        stopAutoplay,
    } = useAutoplaySequence({
        itemIds: MULTIMODAL_STAGE_IDS,
        activeItemId: activeStageId,
        setActiveItemId: setActiveStageId,
        intervalMs:
            MULTIMODAL_AUTOPLAY_INTERVAL_MS,
        threshold: 0.2,
        rootMargin:
            "0px 0px -8% 0px",
    });

    const activeStage =
        multimodalRouteStages.find(
            (stage) =>
                stage.id === activeStageId
        ) || multimodalRouteStages[0];

    return (
        <section
            className="landing-multimodal landing-section landing-section--middle"
            id="multimodal"
            ref={autoplayTargetRef}
        >
            <div className="container public-site-shell">
                <div className="landing-multimodal__heading landing-section-heading">
                    <div>
                        <span className="landing-section-heading__eyebrow">
                            {t("main.landing.multimodal.eyebrow")}
                        </span>

                        <h2 className="landing-section-heading__title">
                            {t("main.landing.multimodal.title")}
                        </h2>
                    </div>

                    <p className="landing-section-heading__intro">
                        {t("main.landing.multimodal.intro")}
                    </p>
                </div>

                <div className="landing-multimodal__layout">
                    <div className="multimodal-route">
                        <div className="multimodal-route__header">
                            <div>
                                <span className="multimodal-route__demo">
                                    <span/>
                                    {t("main.landing.multimodal.demoLabel")}
                                </span>

                                <strong>
                                    {t("main.landing.multimodal.routeName")}
                                </strong>
                            </div>

                            <span className="multimodal-route__mode landing-status-pill">
                                {t("main.landing.multimodal.controlLabel")}
                            </span>
                        </div>

                        <div
                            className={`multimodal-route__timeline${
                                isAutoplaying
                                    ? " multimodal-route__timeline--autoplaying"
                                    : ""
                            }`}
                            role="tablist"
                            aria-label={t(
                                "main.landing.multimodal.tabsAriaLabel"
                            )}
                        >
                            {multimodalRouteStages.map((stage) => {
                                const active = stage.id === activeStageId;

                                return (
                                    <button
                                        className={`multimodal-stage${
                                            active
                                                ? " multimodal-stage--active"
                                                : ""
                                        }`}
                                        type="button"
                                        role="tab"
                                        aria-selected={active}
                                        aria-controls="multimodal-stage-details"
                                        id={`multimodal-stage-${stage.id}`}
                                        onClick={() => selectStage(stage.id)}
                                        onFocus={stopAutoplay}
                                        key={stage.id}
                                    >
                                        <span className="multimodal-stage__top">
                                            <span className="multimodal-stage__icon">
                                                <StageIcon type={stage.icon}/>
                                            </span>

                                            <span className="multimodal-stage__number">
                                                {stage.number}
                                            </span>
                                        </span>

                                        <span className="multimodal-stage__content">
                                            <span>{t(stage.titleKey)}</span>
                                            <strong>{t(stage.routeKey)}</strong>
                                        </span>

                                        <span
                                            className={`multimodal-stage__status multimodal-stage__status--${stage.statusTone}`}
                                        >
                                            {t(stage.statusKey)}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>

                        <div
                            className="multimodal-details"
                            id="multimodal-stage-details"
                            role="tabpanel"
                            aria-labelledby={`multimodal-stage-${activeStage.id}`}
                            key={activeStage.id}
                        >
                            <div className="multimodal-details__header">
                                <span>
                                    {t("main.landing.multimodal.stageLabel", {
                                        number: activeStage.number,
                                    })}
                                </span>

                                <strong>{t(activeStage.routeKey)}</strong>
                            </div>

                            <dl className="multimodal-details__grid">
                                <div className="multimodal-details__participant">
                                    <dt>{t("main.landing.multimodal.details.participant")}</dt>
                                    <dd>{t(activeStage.participantKey)}</dd>
                                </div>

                                <div className="multimodal-details__status">
                                    <dt>{t("main.landing.multimodal.details.status")}</dt>
                                    <dd>{t(activeStage.statusKey)}</dd>
                                </div>

                                <div className="multimodal-details__checkpoint">
                                    <dt>{t("main.landing.multimodal.details.checkpoint")}</dt>
                                    <dd>{t(activeStage.checkpointKey)}</dd>
                                </div>

                                <div className="multimodal-details__documents">
                                    <dt>{t("main.landing.multimodal.details.documents")}</dt>
                                    <dd>{t(activeStage.documentsKey)}</dd>
                                </div>

                                <div className="multimodal-details__schedule">
                                    <dt>{t("main.landing.multimodal.details.schedule")}</dt>
                                    <dd>{t(activeStage.scheduleKey)}</dd>
                                </div>
                            </dl>
                        </div>
                        <section
                            className="multimodal-route__control landing-control-summary"
                            aria-labelledby="multimodal-control-summary-title"
                        >
                            <h3 id="multimodal-control-summary-title">
                                {t(
                                    "main.landing.multimodal.controlSummary.title"
                                )}
                            </h3>

                            <ul>
                                {multimodalControlKeys.map((controlKey) => (
                                    <li key={controlKey}>
                                        <span>
                                            <LandingCheckIcon/>
                                        </span>

                                        {t(controlKey)}
                                    </li>
                                ))}
                            </ul>
                        </section>
                    </div>

                    <LandingSalesCard
                        variant="compact"
                        eyebrow={t(
                            "main.landing.multimodal.sales.eyebrow"
                        )}
                        title={t(
                            "main.landing.multimodal.sales.title"
                        )}
                        description={t(
                            "main.landing.multimodal.sales.description"
                        )}
                        benefits={multimodalBenefitKeys.map(
                            (benefitKey) => ({
                                id: benefitKey,
                                label: t(benefitKey),
                            })
                        )}
                        metrics={[
                            {
                                id: "stages",
                                value: "4",
                                label: t(
                                    "main.landing.multimodal.sales.metrics.stages"
                                ),
                            },
                            {
                                id: "workspace",
                                value: "1",
                                label: t(
                                    "main.landing.multimodal.sales.metrics.workspace"
                                ),
                            },
                        ]}
                        primaryAction={{
                            href: "/register",
                            label: t(
                                "main.landing.multimodal.sales.action"
                            ),
                        }}
                    />
                </div>
            </div>
        </section>
    );
}

export default withNamespaces("translation")(MultimodalSection);
